import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import { fetchApiDataIfNeeded } from '../../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'

import { PaginationWrapper } from 'shs-pagination'
import moment from 'moment'

import * as MASKS from '../../utils/input-masks'

const FORM_TYPE_OPTIONS = [
    { value: 'private_dinner', label: 'Private Dinner' },
    { value: 'multi_day', label: 'Multi-Day' },
    { value: 'weekly_meal', label: 'Weekly Meal' }
]

const DEFAULT_SORT = { key: 'created_at', dir: 'desc' }

const mapStateToProps = (state) => {
    const paginator = state.paginator.leadForms
    let leadForms = state.leadForms
    if (paginator) {
        leadForms = leadForms
            .filter(field => paginator.itemIds.indexOf(field.id) !== -1)
            .sort((a, b) => {
                if (paginator.itemIds.indexOf(a.id) > paginator.itemIds.indexOf(b.id)) {
                    return 1
                }
                return -1
            })
    }
    return {
        leads: leadForms,
        paginator,
        statuses: state.configs.filter(c => c.type === 'LEAD_STATUS')
    }
}

// one line that says when the lead wants a chef, per form type
const eventWhen = (lead) => {
    if (!lead.eventDate) {
        return '—'
    }
    const start = moment(lead.eventDate)
    if (lead.formType === 'multi_day' && lead.endDate) {
        return `${start.format('MMM D')} – ${moment(lead.endDate).format('MMM D, YYYY')}`
    }
    if (lead.formType === 'weekly_meal') {
        return `Starts ${start.format('MMM D, YYYY')}`
    }
    const time = lead.eventTime ? moment(lead.eventTime, 'HH:mm:ss').format(' · h:mm a') : ''
    return `${start.format('MMM D, YYYY')}${time}`
}

const LeadsList = ({ dispatch, leads, paginator, statuses }) => {
    const [filters, setFilters] = useState({ formType: '', statusId: '' })
    const [sort, setSort] = useState(DEFAULT_SORT)
    // the pagination wrapper owns page/perpage/search; keep its last params so the
    // filter and sort controls can refetch without dropping the current search
    const lastParams = useRef({})

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/lead-forms', 'LIST'), { sortkey: DEFAULT_SORT.key, sortdir: DEFAULT_SORT.dir }))
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/configs', 'LIST'), { filter: ['type|LEAD_STATUS'] }))
    }, [dispatch])

    const fetchLeads = (baseParams, nextFilters, nextSort) => {
        lastParams.current = baseParams
        // the wrapper also sends its unused sort/filter control state as `filters`
        const { filters: unusedControls, ...params } = baseParams
        const filter = []
        if (nextFilters.formType) {
            filter.push(`form_type|${nextFilters.formType}`)
        }
        if (nextFilters.statusId) {
            filter.push(`status_id|${nextFilters.statusId}`)
        }
        if (filter.length > 0) {
            params.filter = filter
        }
        params.sortkey = nextSort.key
        params.sortdir = nextSort.dir
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/lead-forms', 'LIST'), params))
    }

    const handlePaginationUpdate = (paginationParams) => {
        fetchLeads(paginationParams, filters, sort)
    }

    const handleFilterChange = (field, value) => {
        const nextFilters = { ...filters, [field]: value }
        setFilters(nextFilters)
        fetchLeads({ ...lastParams.current, page: 1 }, nextFilters, sort)
    }

    const handleSort = (key) => {
        const nextSort = sort.key === key
            ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: key === 'last_name' ? 'asc' : 'desc' }
        setSort(nextSort)
        fetchLeads({ ...lastParams.current, page: 1 }, filters, nextSort)
    }

    const renderSortHeader = (label, key) => {
        const arrow = sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''
        return (
            <th className="sortable" onClick={() => handleSort(key)}>
                {label}{arrow}
            </th>
        )
    }

    const renderLeadRow = (lead, index) => {
        const typeLabel = (FORM_TYPE_OPTIONS.find(t => t.value === lead.formType) || {}).label || ''
        const location = [lead.city, lead.state].filter(Boolean).join(', ')
        const viewPath = buildRoutePath(routes.adminLeadsView.path, { leadId: lead.id })
        return (
            <tr key={lead.id}>
                <td><span>{index}.</span></td>
                <td>
                    {moment(lead.createdAt).format('MMM D, YYYY')}
                    <div className="lead-list__sub">{moment(lead.createdAt).fromNow()}</div>
                </td>
                <td>
                    <Link to={viewPath} className="lead-list__name">{`${lead.firstName} ${lead.lastName}`}</Link>
                    <div className="lead-list__sub">{lead.email}</div>
                </td>
                <td><span className="lead-type-badge">{typeLabel}</span></td>
                <td>{eventWhen(lead)}</td>
                <td className="nowrap">{lead.headcount ? `${lead.headcount} ${lead.formType === 'weekly_meal' ? 'portions' : 'guests'}` : '—'}</td>
                <td>{location || '—'}</td>
                <td className="nowrap">{lead.phone ? MASKS.phoneMask.resolve(String(lead.phone).replace(/\D/g, '')) : '—'}</td>
                <td>{lead._computed.statusDisplay}</td>
                <td>
                    {lead._computed.ticketId
                        ? <Link to={buildRoutePath(routes.adminTicketsView.path, { ticketId: lead._computed.ticketId })}>Ticket {lead._computed.ticketId}</Link>
                        : <Link to={viewPath}>View</Link>
                    }
                </td>
            </tr>
        )
    }

    const renderFilters = () => (
        <div className="lead-list__filters">
            <select className="form-select" value={filters.formType} onChange={evt => handleFilterChange('formType', evt.target.value)} aria-label="Filter by type">
                <option value="">All types</option>
                {FORM_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="form-select" value={filters.statusId} onChange={evt => handleFilterChange('statusId', evt.target.value)} aria-label="Filter by status">
                <option value="">All statuses</option>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.display}</option>)}
            </select>
            <span className="lead-list__hint">Search matches name, email, phone, city, state, or zip.</span>
        </div>
    )

    const renderLeads = () => {
        if (!paginator) {
            return null
        }
        const allLeads = leads.map((lead, idx) => renderLeadRow(lead, idx + ((paginator.currentPage - 1) * paginator.perPage) + 1))
        return (
            <PaginationWrapper
                paginator={paginator}
                controls={{ search: { enabled: true, collapsed: false } }}
                callbackFX={handlePaginationUpdate}
                countOptions={[10, 25, 50, 100]}>
                <table className="table table-striped table-hover lead-list">
                    <thead>
                        <tr>
                            <th>#</th>
                            {renderSortHeader('Received', 'created_at')}
                            {renderSortHeader('Name', 'last_name')}
                            <th>Type</th>
                            {renderSortHeader('Event', 'event_date')}
                            <th>Size</th>
                            <th>Location</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allLeads.length > 0
                            ? allLeads
                            : <tr><td colSpan={10} className="lead-list__empty">No leads match these filters.</td></tr>
                        }
                    </tbody>
                </table>
            </PaginationWrapper>
        )
    }

    return (
        <>
            <div className="section-header">
                <span className="title">Leads</span>
            </div>
            {renderFilters()}
            {renderLeads()}
        </>
    )
}

export default connect(mapStateToProps)(LeadsList)
